import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { UserUtils } from '@/domain/utils/userUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';

/**
 * The tappable author row of a roadmap card.
 *
 * Ports `roadmap_list_item.dart:124-151`: the author avatar + "Idea from
 * {name}" label. Per the story's deferred-actions rule this is the ONLY real
 * action on the card — tapping routes the parent's `onUserTap(userId)` to
 * user-details.
 */
interface RoadmapItemAuthorRowProps {
  itemId: string;
  userId: string;
  userName: string;
  onUserTap: (userId: string) => void;
}

export const RoadmapItemAuthorRow: React.FC<RoadmapItemAuthorRowProps> = ({
  itemId,
  userId,
  userName,
  onUserTap,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  return (
    <div
      onClick={() => onUserTap(userId)}
      {...qaAttr({ id: `roadmap-item-user-${itemId}` })}
      style={{
        marginTop: responsive.spacing['2xl'],
        display: 'flex',
        alignItems: 'center',
        gap: responsive.gap.xs,
        cursor: 'pointer',
      }}
    >
      <CachedImage
        src={UserUtils.generateUserProfileImageUrl(userId)}
        alt={userName}
        style={{
          width: responsive.avatar.medium,
          height: responsive.avatar.medium,
          borderRadius: responsive.borderRadius.round,
          objectFit: 'cover',
        }}
      />
      <span
        style={{
          color: theme.text.primary,
          fontSize: responsive.font.base,
        }}
      >
        {l10n.roadmap.ideaFrom.replace('{name}', userName)}
      </span>
    </div>
  );
};
